import { useState, useMemo, useCallback, useEffect } from 'react';

// ClarityOS Five States - clockwise from top (brighter colors for better contrast)
const STATES = [
  { key: 'ground', label: 'Ground', color: '#3D8B66', colorLight: '#5BB88A', colorBright: '#6FD4A0', angle: -90, desc: 'Identity, values, stability' },
  { key: 'observe', label: 'Observe', color: '#2B6A94', colorLight: '#4091C4', colorBright: '#5AAEE0', angle: -18, desc: 'Self-awareness, blind spots' },
  { key: 'connect', label: 'Connect', color: '#9B5088', colorLight: '#C070AC', colorBright: '#D88BC4', angle: 54, desc: 'Relational style, influence' },
  { key: 'execute', label: 'Execute', color: '#B54D24', colorLight: '#D86A3C', colorBright: '#F08050', angle: 126, desc: 'Cognitive strength, drive' },
  { key: 'integrate', label: 'Integrate', color: '#4D7636', colorLight: '#6B9A50', colorBright: '#85B86A', angle: 198, desc: 'Synthesis, wisdom' }
];

const AMBER = '#C8860A';
const LABEL_COLOR = '#A09888';

// Generate teardrop petal path using cubic bezier curves
function generatePetalPath(length, maxWidth) {
  if (length <= 5) return '';
  
  // Petal points upward (negative Y in SVG)
  // Width scales with length - wider petals for longer lengths
  const widthRatio = Math.max(0.35, (length / maxWidth) * 0.5);
  const width = maxWidth * widthRatio;
  const tipY = -length;
  
  // Control points for smooth organic teardrop/lotus petal curve
  // Petal should bulge more in the middle and taper elegantly to tip
  const bulgeY = -length * 0.35;      // Where petal is widest
  const neckY = -length * 0.75;       // Where it starts to taper
  const tipControlY = -length * 0.92; // Control point near tip
  
  // Create organic lotus petal shape
  const path = `
    M 0,0
    C ${width * 0.4},${bulgeY * 0.5} ${width * 0.95},${bulgeY} ${width * 0.85},${neckY}
    Q ${width * 0.5},${tipControlY} 0,${tipY}
    Q ${-width * 0.5},${tipControlY} ${-width * 0.85},${neckY}
    C ${-width * 0.95},${bulgeY} ${-width * 0.4},${bulgeY * 0.5} 0,0
    Z
  `;
  
  return path;
}

// Convert degrees to radians
function degToRad(deg) {
  return (deg * Math.PI) / 180;
}

// Get position at angle and distance from center
function getPosition(cx, cy, angle, distance) {
  const rad = degToRad(angle);
  return {
    x: cx + distance * Math.cos(rad),
    y: cy + distance * Math.sin(rad)
  };
}

// Single Petal Component
function Petal({ 
  state, 
  score, 
  maxLength, 
  cx, 
  cy, 
  isActive, 
  isHovered,
  onHover, 
  onClick,
  animationDelay,
  animate 
}) {
  const [isAnimated, setIsAnimated] = useState(!animate);
  
  useEffect(() => {
    if (animate) {
      const timer = setTimeout(() => setIsAnimated(true), animationDelay);
      return () => clearTimeout(timer);
    }
  }, [animate, animationDelay]);
  
  const petalLength = (score / 100) * maxLength;
  const path = generatePetalPath(isAnimated ? petalLength : 0, maxLength);
  
  // Enhanced visibility for active/hovered states
  const isHighlighted = isActive || isHovered;
  const opacity = isHighlighted ? 1.0 : 0.45;  // Dim non-active petals more
  const strokeWidth = isHighlighted ? 3 : 1.5;
  
  // Score label position - at tip of petal
  const scoreLabelPos = getPosition(cx, cy, state.angle, petalLength + 16);
  const stateLabelPos = getPosition(cx, cy, state.angle, petalLength + 38);
  
  // Adjust text anchor based on angle
  const getTextAnchor = (angle) => {
    if (angle > -45 && angle < 45) return 'start';
    if (angle > 135 || angle < -135) return 'end';
    return 'middle';
  };
  
  const getDy = (angle) => {
    if (angle > 45 && angle < 135) return 12;
    if (angle < -45 && angle > -135) return -4;
    return 4;
  };

  // Spotlight glow intensity - much stronger for active state
  const glowIntensity = isHighlighted ? 35 : 0;
  const glowColor = isHighlighted ? state.colorBright || state.colorLight : state.color;

  return (
    <g 
      className="petal-group cursor-pointer"
      onClick={() => onClick?.(state.key)}
      onMouseEnter={() => onHover?.(state.key)}
      onMouseLeave={() => onHover?.(null)}
      style={{ transformOrigin: `${cx}px ${cy}px` }}
    >
      {/* Outer spotlight glow - largest blur ring */}
      {isHighlighted && (
        <g transform={`translate(${cx}, ${cy}) rotate(${state.angle + 90})`}>
          <path
            d={path}
            fill={glowColor}
            fillOpacity={0.25}
            stroke="none"
            style={{
              filter: `blur(25px)`,
              transform: 'scale(1.4)'
            }}
          />
        </g>
      )}
      
      {/* Middle spotlight glow */}
      {isHighlighted && (
        <g transform={`translate(${cx}, ${cy}) rotate(${state.angle + 90})`}>
          <path
            d={path}
            fill={glowColor}
            fillOpacity={0.5}
            stroke="none"
            style={{
              filter: `blur(12px)`,
              transform: 'scale(1.2)'
            }}
          />
        </g>
      )}
      
      {/* Petal shape */}
      <g transform={`translate(${cx}, ${cy}) rotate(${state.angle + 90})`}>
        <defs>
          <radialGradient 
            id={`petal-gradient-${state.key}`}
            cx="50%" 
            cy="0%" 
            r="100%"
            gradientUnits="objectBoundingBox"
          >
            <stop offset="0%" stopColor={isHighlighted ? state.colorBright : state.colorLight} stopOpacity={isHighlighted ? 1 : 0.9} />
            <stop offset="60%" stopColor={state.colorLight} stopOpacity={isHighlighted ? 0.95 : 0.8} />
            <stop offset="100%" stopColor={state.color} stopOpacity="0.15" />
          </radialGradient>
        </defs>
        
        <path
          d={path}
          fill={`url(#petal-gradient-${state.key})`}
          stroke={isHighlighted ? state.colorBright : state.color}
          strokeWidth={strokeWidth}
          opacity={opacity}
          style={{
            transition: 'all 0.4s ease-out',
            filter: isHighlighted ? `drop-shadow(0 0 ${glowIntensity}px ${glowColor})` : 'none'
          }}
        />
      </g>
      
      {/* Score label at petal tip */}
      {score > 0 && isAnimated && (
        <text
          x={scoreLabelPos.x}
          y={scoreLabelPos.y}
          textAnchor={getTextAnchor(state.angle)}
          dy={getDy(state.angle)}
          fill={isHighlighted ? state.colorBright : state.colorLight}
          fontSize={isHighlighted ? 15 : 12}
          fontFamily="'DM Mono', monospace"
          fontWeight="600"
          style={{ 
            transition: 'all 0.2s ease',
            filter: isHighlighted ? `drop-shadow(0 0 4px ${state.colorBright}80)` : 'none'
          }}
        >
          {score}
        </text>
      )}
      
      {/* State label beyond score - only show when there's actual score data */}
      {score > 0 && isAnimated && (
        <text
          x={stateLabelPos.x}
          y={stateLabelPos.y}
          textAnchor={getTextAnchor(state.angle)}
          dy={getDy(state.angle)}
          fill={isHighlighted ? state.colorBright : LABEL_COLOR}
          fontSize={isHighlighted ? 12 : 11}
          fontFamily="Inter, sans-serif"
          fontWeight={isHighlighted ? 600 : 500}
          letterSpacing="0.05em"
          style={{ 
            textTransform: 'uppercase',
            transition: 'all 0.3s ease',
            filter: isHighlighted ? `drop-shadow(0 0 3px ${state.colorBright}60)` : 'none'
          }}
        >
          {state.label}
        </text>
      )}
    </g>
  );
}

// Main Lotus Map Component
export default function StrataMap({ 
  scores = {}, 
  size = 400, 
  activeState = null,
  onStateClick = null,
  onStateHover = null,
  showLabels = true,
  animated = true 
}) {
  const [hoveredState, setHoveredState] = useState(null);
  const [hasAnimated, setHasAnimated] = useState(!animated);
  
  useEffect(() => {
    if (animated && !hasAnimated) {
      // Start animation after a brief delay
      const timer = setTimeout(() => setHasAnimated(true), 100);
      return () => clearTimeout(timer);
    }
  }, [animated, hasAnimated]);
  
  const cx = size / 2;
  const cy = size / 2;
  const maxPetalLength = (size / 2) - 60; // Leave room for labels
  
  const handleHover = useCallback((key) => {
    setHoveredState(key);
    onStateHover?.(key);
  }, [onStateHover]);
  
  const handleClick = useCallback((key) => {
    onStateClick?.(key);
  }, [onStateClick]);
  
  const hasData = Object.values(scores).some(v => v > 0);

  return (
    <div 
      className="relative" 
      style={{ width: size, height: size }}
      data-testid="strata-map"
    >
      <svg 
        width={size} 
        height={size} 
        viewBox={`0 0 ${size} ${size}`}
        className="overflow-visible"
      >
        <defs>
          {/* Ambient glow at center */}
          <radialGradient id="center-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={AMBER} stopOpacity="0.15" />
            <stop offset="50%" stopColor={AMBER} stopOpacity="0.04" />
            <stop offset="100%" stopColor={AMBER} stopOpacity="0" />
          </radialGradient>
        </defs>
        
        {/* Background ambient glow */}
        <circle
          cx={cx}
          cy={cy}
          r={maxPetalLength * 0.8}
          fill="url(#center-glow)"
        />
        
        {/* Petals - render in order for proper overlap */}
        {STATES.map((state, index) => (
          <Petal
            key={state.key}
            state={state}
            score={scores[state.key] || 0}
            maxLength={maxPetalLength}
            cx={cx}
            cy={cy}
            isActive={activeState === state.key}
            isHovered={hoveredState === state.key}
            onHover={handleHover}
            onClick={handleClick}
            animationDelay={index * 100}
            animate={hasAnimated && hasData}
          />
        ))}
        
        {/* Center core - amber disc */}
        <circle
          cx={cx}
          cy={cy}
          r={12}
          fill={AMBER}
          className={hasAnimated ? 'animate-fade-in' : 'opacity-0'}
          style={{
            filter: `drop-shadow(0 0 8px ${AMBER}60)`,
            transition: 'all 0.5s ease'
          }}
        />
        
        {/* Inner glow ring */}
        <circle
          cx={cx}
          cy={cy}
          r={16}
          fill="none"
          stroke={AMBER}
          strokeWidth="1"
          strokeOpacity="0.3"
          className={hasAnimated ? 'animate-fade-in' : 'opacity-0'}
        />
        
        {/* Empty state - bud indication */}
        {!hasData && (
          <g className="empty-state">
            <circle
              cx={cx}
              cy={cy}
              r={30}
              fill="none"
              stroke={AMBER}
              strokeWidth={1}
              strokeOpacity={0.2}
              strokeDasharray="4 4"
              className="animate-pulse-slow"
            />
            {/* State labels positioned around the empty lotus */}
            {STATES.map((state) => {
              const labelDistance = 70; // Fixed distance from center for empty state
              const labelPos = getPosition(cx, cy, state.angle, labelDistance);
              const getTextAnchor = (angle) => {
                if (angle > -45 && angle < 45) return 'start';
                if (angle > 135 || angle < -135) return 'end';
                return 'middle';
              };
              return (
                <text
                  key={state.key}
                  x={labelPos.x}
                  y={labelPos.y}
                  textAnchor={getTextAnchor(state.angle)}
                  dominantBaseline="middle"
                  fill={LABEL_COLOR}
                  fontSize="10"
                  fontFamily="Inter, sans-serif"
                  fontWeight="500"
                  letterSpacing="0.05em"
                  style={{ textTransform: 'uppercase' }}
                  opacity={0.7}
                >
                  {state.label}
                </text>
              );
            })}
            <text
              x={cx}
              y={cy + 100}
              textAnchor="middle"
              fill={LABEL_COLOR}
              fontSize="12"
              fontFamily="Inter, sans-serif"
              opacity={0.6}
            >
              Add sources to bloom your lotus
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}

// Mini version for cards
export function StrataMapMini({ scores = {}, size = 120 }) {
  return (
    <StrataMap 
      scores={scores} 
      size={size} 
      showLabels={false}
      animated={false}
    />
  );
}

// Full bloom reference component
export function StrataMapFullBloom({ size = 200 }) {
  const fullScores = {
    ground: 100,
    observe: 100,
    connect: 100,
    execute: 100,
    integrate: 100
  };
  
  return (
    <div className="text-center">
      <StrataMap 
        scores={fullScores} 
        size={size} 
        showLabels={false}
        animated={false}
      />
      <p className="text-amber-500 text-sm font-display mt-2">Your Full Bloom</p>
    </div>
  );
}
